import type { TAccumulator, TFinisher, TFinishingReducer, TReducer, TSupplier } from "./Header.ts";

import {
  collecting as langCollecting,
  counting as langCounting,
  groupingBy as langGroupingBy,
  joining as langJoining,
  toArray as langToArray,
  toMap as langToMap,
  toRecord as langToRecord,
  toSet as langToSet,
  toSingleSet as langToSingleSet,
} from "@de-xima/xima-common-js-lang";

/**
 * Returns a {@link TFinishingReducer} that concatenates the input elements into
 * a string, in encounter order.
 * @deprecated Use module `@de-xima/xima-common-js-lang`.
 * @param separator The delimiter to be used between each element.
 * @returns A {@link TFinishingReducer} that concatenates the input elements into
 * a string, in encounter order.
 */
export function collecting<T, A, R>(
  supplier: TSupplier<A>,
  accumulator: TAccumulator<T, A>,
  finisher: TFinisher<A, R>,
): TFinishingReducer<T, R> {
  return langCollecting(supplier, accumulator, finisher);
}

/**
 * Returns a {@link TFinishingReducer} that concatenates the input elements into
 * a string, in encounter order.
 * @deprecated Use module `@de-xima/xima-common-js-lang`.
 * @param separator The delimiter to be used between each element.
 * @param stringConverter A converter that turns a value into a string. Defaults to
 * {@link String String()}.
 * @returns A {@link TFinishingReducer} that concatenates the input elements into
 * a string, in encounter order.
 */
export function joining<T>(
  separator: string,
  stringConverter: (item: T) => string = String,
): TFinishingReducer<T, string> {
  return langJoining(separator, stringConverter);
}

/**
 * Returns a {@link TReducer} implementing a `counting` operation, returning
 * the number of elements. If no elements are present in the input, returns `0`.
 * @deprecated Use module `@de-xima/xima-common-js-lang`.
 * @returns A {@link TReducer} that counts the input elements.
 */
export function counting<T>(): TReducer<T, number> {
  return langCounting();
}

/**
 * Returns a {@link TReducer} implementing a `group by` operation on
 * input elements of type {@code T}, grouping elements according to a
 * classification function, and returning the results in a map.
 * @deprecated Use module `@de-xima/xima-common-js-lang`.
 * @param classifier The classifier function mapping input elements to keys.
 * @returns a {@link TReducer} implementing the group-by operation
 */
export function groupingBy<T, K>(classifier: (item: T) => K): TReducer<T, Map<K, T[]>> {
  return langGroupingBy(classifier);
}

/**
 * Returns a {@link TReducer} that accumulates elements into a list, in the
 * order the elements are encountered.
 * @deprecated Use module `@de-xima/xima-common-js-lang`.
 * @returns A {@link TReducer} which collects elements into a set.
 */
export function toList<T>(): TReducer<T, T[]> {
  return langToArray();
}

/**
 * Returns a {@link TReducer} that accumulates elements into a set. When the
 * same element occurs multiple times, the last encountered element is used.
 * @deprecated Use module `@de-xima/xima-common-js-lang`.
 * @returns A {@link TReducer} which collects elements into a set.
 */
export function toSet<T>(): TReducer<T, Set<T>> {
  return langToSet();
}

/**
 * Returns a {@link TReducer} that accumulates multiple sets into a single set.
 * When the same element occurs multiple times, the last encountered element is
 * used.
 * @deprecated Use module `@de-xima/xima-common-js-lang`.
 * @returns A {@link TReducer} which collects multiple sets into a single set.
 */
export function toSingleSet<T>(): TReducer<Iterable<T>, Set<T>> {
  return langToSingleSet();
}

/**
 * Returns a {@link TReducer} that accumulates elements into a map whose keys
 * and values are the result of applying the provided mapping functions to the
 * input elements.
 *
 * When more than one element maps to the same key, the last encountered element
 * is used.
 * @deprecated Use module `@de-xima/xima-common-js-lang`.
 * @param keyMapper A mapping function to produce keys.
 * @param valueMapper A mapping function to produce values.
 * @returns a {@link TReducer} which collects elements into a map whose keys
 * are the result of applying a key mapping function to the input elements, and
 * whose keys and values are the result of applying a mapping functions to the
 * input elements.
 */
export function toMap<T, K, V>(keyMapper: (item: T) => K, valueMapper: (item: T) => V): TReducer<T, Map<K, V>> {
  return langToMap(keyMapper, valueMapper);
}

/**
 * Returns a {@link TReducer} that accumulates elements into a record whose
 * keys and values are the result of applying the provided mapping functions to
 * the input elements.
 *
 * When more than one element maps to the same key, the last encountered element
 * is used.
 * @deprecated Use module `@de-xima/xima-common-js-lang`.
 * @param keyMapper A mapping function to produce keys.
 * @param valueMapper A mapping function to produce values.
 * @returns a {@link TReducer} which collects elements into a record whose keys
 * are the result of applying a key mapping function to the input elements, and
 * whose keys and values are the result of applying a mapping functions to the
 * input elements.
 */
export function toRecord<T, P extends PropertyKey, V>(
  keyMapper: (item: T) => P,
  valueMapper: (item: T) => V,
): TReducer<T, Partial<Record<P, V>>> {
  return langToRecord(keyMapper, valueMapper);
}
